import React, {startTransition, useEffect, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {FootballFormFrame, DetailSection} from '@/components/football';
import Input from '@/components/ui/Input';
import {useToast} from '@/context/ToastContext';
import {useWorkspace} from '@/context/WorkspaceContext';
import {PlayerDetail, PlayerMutationInput, playersV2API, PlayerScope} from '@/features/players/api';
import {parsePlayerReturnTo, parsePlayerTab} from '@/features/players/playerUrlState';
import {playerKeys} from '@/features/players/queries';
import {getV2Error} from '@/services/api/v2';
import {approveNextDirtyNavigation} from '@/context/workspaceDirtyRegistry';

interface PlayerFormPageProps { mode: 'squad' | 'scouting' }

export interface PlayerFormValues {
  name: string;
  age: number;
  position: 'Forward' | 'Midfielder' | 'Defender' | 'Goalkeeper';
  team: string;
  photoUrl: string;
  photoFile: File | null;
}

const emptyValues: PlayerFormValues = {
  name: '', age: 0, position: 'Forward', team: '', photoUrl: '', photoFile: null,
};

const fieldErrors = (error: unknown): Record<string, string[]> => {
  const details = getV2Error(error).details;
  const result: Record<string, string[]> = {};
  const fieldNames: Record<string, string> = {photo_url: 'photoUrl', photo: 'photoFile'};
  Object.entries(details).forEach(([field, value]) => {
    if (Array.isArray(value) && value.every(item => typeof item === 'string')) result[fieldNames[field] ?? field] = value;
  });
  return Object.keys(result).length ? result : {form: [getV2Error(error).message]};
};

const PlayerFormPage: React.FC<PlayerFormPageProps> = ({mode}) => {
  const {id} = useParams<{id: string}>();
  const editing = Boolean(id);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {showToast} = useToast();
  const {currentWorkspace} = useWorkspace();
  const workspaceId = currentWorkspace?.id ?? '';
  const root = mode === 'squad' ? '/players' : '/scouting';
  const scope: PlayerScope = mode === 'squad' ? 'squad' : 'external';
  const returnTo = parsePlayerReturnTo(searchParams.get('from'), mode);
  const requestedTab = parsePlayerTab(searchParams.get('tab'));
  const [values, setValues] = useState(emptyValues);
  const [initialValues, setInitialValues] = useState(emptyValues);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const detail = useQuery({
    queryKey: playerKeys.detail(workspaceId, id ?? ''),
    queryFn: () => playersV2API.detail<PlayerDetail>(id!, workspaceId),
    enabled: editing && Boolean(workspaceId),
  });

  useEffect(() => {
    setValues(emptyValues);
    setInitialValues(emptyValues);
    setErrors({});
  }, [workspaceId, mode, id]);

  useEffect(() => {
    if (!detail.data || detail.data.data.id !== id) return;
    const player = detail.data.data;
    const loaded: PlayerFormValues = {
      name: player.name, age: player.age,
      position: player.position as PlayerFormValues['position'],
      team: player.team ?? '', photoUrl: player.photo_url ?? '', photoFile: null,
    };
    setValues(loaded);
    setInitialValues(loaded);
  }, [detail.data, id]);

  const mutation = useMutation({
    mutationFn: (input: PlayerMutationInput) => editing
      ? playersV2API.update(id!, input, workspaceId)
      : playersV2API.create(input, workspaceId),
    onSuccess: async response => {
      const playerId = editing ? id! : response.data.id;
      await Promise.all([
        queryClient.invalidateQueries({queryKey: playerKeys.all(workspaceId)}),
        ...(editing ? [queryClient.invalidateQueries({queryKey: playerKeys.detail(workspaceId, id!)})] : []),
      ]);
      showToast(editing ? 'Player updated' : 'Player created', 'success');
      const next = new URLSearchParams();
      if (editing && requestedTab !== 'overview') next.set('tab', requestedTab);
      next.set('from', returnTo);
      approveNextDirtyNavigation();
      startTransition(() => navigate(`${root}/${playerId}?${next.toString()}`, {replace: true}));
    },
    onError: error => setErrors(fieldErrors(error)),
  });

  const set = <K extends keyof PlayerFormValues>(key: K, value: PlayerFormValues[K]) => {
    setValues(previous => ({...previous, [key]: value}));
    setErrors(previous => {
      if (!previous[key]) return previous;
      const next = {...previous};
      delete next[key];
      return next;
    });
  };
  const dirty = JSON.stringify({...values, photoFile: null}) !== JSON.stringify({...initialValues, photoFile: null})
    || values.photoFile !== null;
  const detailParams = new URLSearchParams();
  if (requestedTab !== 'overview') detailParams.set('tab', requestedTab);
  detailParams.set('from', returnTo);
  const cancelHref = editing ? `${root}/${id}?${detailParams.toString()}` : returnTo;

  if (editing && detail.isPending) return <DetailSection state={{status: 'loading', message: 'Loading player form...'}} />;
  if (editing && detail.isError) return <DetailSection state={{status: 'error', message: 'Unable to load this player.', retry: () => { void detail.refetch(); }}} />;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const clientErrors: Record<string, string[]> = {};
    if (!values.name.trim()) clientErrors.name = ['Name is required.'];
    if (!Number.isInteger(values.age) || values.age < 16 || values.age > 40) clientErrors.age = ['Age must be between 16 and 40.'];
    if (scope === 'external' && !values.team.trim()) clientErrors.team = ['Team is required for a scouting player.'];
    if (values.photoFile && !values.photoFile.type.startsWith('image/')) clientErrors.photo = ['Photo must be an image file.'];
    if (Object.keys(clientErrors).length) { setErrors(clientErrors); return; }
    setErrors({});
    mutation.mutate({
      scope, name: values.name.trim(), age: values.age, position: values.position,
      ...(values.team.trim() ? {team: values.team.trim()} : {}),
      ...(values.photoUrl.trim() ? {photoUrl: values.photoUrl.trim()} : {}),
      ...(values.photoFile ? {photoFile: values.photoFile} : {}),
    });
  };

  return (
    <FootballFormFrame
      guardKey={`player-form-${workspaceId}-${mode}-${id || 'new'}`}
      guardLabel="player form"
      title={editing ? 'Edit player' : 'Add player'}
      eyebrow={mode === 'squad' ? 'Squad' : 'Scouting'}
      errors={errors} dirty={dirty} submitting={mutation.isPending}
      cancelHref={cancelHref} submitLabel={editing ? 'Save player' : 'Create player'} onSubmit={submit}
      sections={(
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Input id="name" label="Name" value={values.name} error={errors.name?.[0]} onChange={event => set('name', event.target.value)} />
          <Input id="age" label="Age" type="number" min={16} max={40} value={values.age || ''} error={errors.age?.[0]} onChange={event => set('age', Number(event.target.value))} />
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="position">Position
            <select id="position" className="input-field mt-1 min-h-11 w-full" value={values.position} onChange={event => set('position', event.target.value as PlayerFormValues['position'])}>
              {['Forward', 'Midfielder', 'Defender', 'Goalkeeper'].map(position => <option key={position}>{position}</option>)}
            </select>
          </label>
          <Input id="team" label="Team" value={values.team} error={errors.team?.[0]} onChange={event => set('team', event.target.value)} />
          <Input id="photoUrl" label="Photo URL" type="url" value={values.photoUrl} error={errors.photoUrl?.[0]} onChange={event => set('photoUrl', event.target.value)} />
          <div><label htmlFor="photoFile" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">Photo</label><input key={`${workspaceId}-${mode}-${id || 'new'}`} id="photoFile" type="file" accept="image/*" className="input-field min-h-11 w-full" onChange={event => set('photoFile', event.target.files?.[0] ?? null)} />{errors.photoFile && <p className="mt-1 text-sm text-danger-600">{errors.photoFile[0]}</p>}</div>
        </div>
      )}
    />
  );
};

export default PlayerFormPage;
